import type { AppModalType, ModalProps } from "@app/AppModalTypes";
import type { EntitlementRejectedModalType } from "@v2/types/modalTypes";
import type { RequiredKeys } from "replo-utils/lib/types";
import type { ErrorDetails } from "schemas/errors";

import { analytics } from "@v2/features/infra/analytics/tracking";
import { createShallowStore } from "replo-utils/react/zustand";

// ---------------------------------------------------------------------------
// Payload types
// ---------------------------------------------------------------------------

export type OpenModalPayload = {
  [K in AppModalType]: RequiredKeys<ModalProps<K>> extends never
    ? keyof ModalProps<K> extends never
      ? { type: K; props?: never }
      : { type: K; props?: ModalProps<K> }
    : { type: K; props: ModalProps<K> };
}[AppModalType];

type CloseModalPayload = {
  type?: AppModalType;
};

export type CloseModalsWithExclusionsPayload = {
  typesToExclude?: AppModalType[];
};

// ---------------------------------------------------------------------------
// Error details — typed to match what getErrorDetails returns, without
// importing from v1. The caller (error-handling bridge) extracts the details
// and passes them in already resolved.
// ---------------------------------------------------------------------------

type BillingEntitlementErrorType =
  | "billing.addPublishedElements"
  | "billing.addShopifyThemeProject"
  | "billing.addReploSite";

export type ModalErrorDetails =
  | {
      errorDetails: ErrorDetails;
      type: "fullPageModal" | "toast";
      errorKey?: string;
      endpointName?: string;
    }
  | {
      errorDetails: ErrorDetails;
      type: BillingEntitlementErrorType;
      errorKey: EntitlementRejectedModalType;
    }
  | {
      type: "shopifyStorePassword";
      errorKey: "passwordRequired" | "wrongPassword";
    }
  | { type: "none"; errorKey?: string; endpointName?: string }
  | null;

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

type ModalState = {
  [Property in AppModalType]: keyof ModalProps<Property> extends never
    ? never
    : ModalProps<Property>;
};

type ModalStoreState = {
  modals: Partial<ModalState>;
  actions: {
    openModal: (payload: OpenModalPayload) => void;
    closeModal: (payload: CloseModalPayload) => void;
    closeModalsWithExclusions: (
      payload: CloseModalsWithExclusionsPayload,
    ) => void;
    handleErrorDetails: (payload: {
      details: ModalErrorDetails;
      billingPlanErrorUsage?: { current: number; maximum: number };
      isElementPublished?: boolean;
    }) => void;
    clearAllModals: () => void;
  };
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const useModalStore = createShallowStore<ModalStoreState>((set) => {
  return {
    modals: {},
    actions: {
      openModal: (payload) => {
        const { type } = payload;
        const props = "props" in payload ? payload.props : {};
        analytics.logEvent("openModal", payload);
        set((state) => ({
          modals: { ...state.modals, [type]: props },
        }));
      },

      closeModal: (payload) => {
        const { type } = payload;
        if (!type) {
          set({ modals: {} });
          return;
        }
        set((state) => {
          const next = { ...state.modals };
          delete next[type];
          return { modals: next };
        });
      },

      closeModalsWithExclusions: (payload) => {
        const { typesToExclude } = payload;
        if (typesToExclude) {
          set((state) => {
            const preserved = Object.fromEntries(
              Object.entries(state.modals).filter(([key]) => {
                return typesToExclude.includes(key as AppModalType);
              }),
            ) as Partial<ModalState>;
            return { modals: preserved };
          });
        } else {
          set({ modals: {} });
        }
      },

      handleErrorDetails: (payload) => {
        const { details, billingPlanErrorUsage, isElementPublished } = payload;
        if (!details) {
          return;
        }

        if (
          details.type === "billing.addPublishedElements" ||
          details.type === "billing.addShopifyThemeProject" ||
          details.type === "billing.addReploSite"
        ) {
          const props = { explanationMessage: details.errorDetails.header };
          analytics.logEvent("openModal", {
            type: details.errorKey,
            props,
          });
          set((state) => ({
            modals: { ...state.modals, [details.errorKey]: props },
          }));
          return;
        }

        if (details.type === "none") {
          return;
        }

        if (details.errorKey?.startsWith("billingPlan")) {
          set((state) => ({
            modals: {
              ...state.modals,
              billingErrorModal: {
                billingPlanMessageKey: details.errorKey,
                usage: billingPlanErrorUsage,
                isElementPublished,
              },
            },
          }));
        } else if (details.type === "fullPageModal") {
          set((state) => ({
            modals: {
              ...state.modals,
              fullPageErrorModal: {
                details: {
                  header: details.errorDetails.header,
                  message: details.errorDetails.message,
                  callToAction: details.errorDetails.callToAction,
                  isRecoverable: details.errorDetails.isRecoverable,
                },
              },
            },
          }));
        } else if (details.type === "shopifyStorePassword") {
          set((state) => ({
            modals: {
              ...state.modals,
              storePasswordRequiredModal: {
                type: details.errorKey,
              },
            },
          }));
        }
      },

      clearAllModals: () => {
        set({ modals: {} });
      },
    },
  };
});

// ---------------------------------------------------------------------------
// Exported store reference (for getState() access in the bridge)
// ---------------------------------------------------------------------------

export const modalStore = useModalStore;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const useModals = () => useModalStore((state) => state.modals);

export const useAreModalsOpen = () => {
  return useModalStore((state) => Object.keys(state.modals).length > 0);
};

export const useModalActions = () => useModalStore((state) => state.actions);

export const useModal = () => {
  const modals = useModals();
  const { openModal, closeModal, closeModalsWithExclusions } =
    useModalActions();
  return { modals, openModal, closeModal, closeModalsWithExclusions };
};

export const useOpenModal = () => {
  const { openModal } = useModalActions();
  return openModal;
};
